import React, { useCallback, useEffect, useRef, useState } from 'react';
import BarcodeScanner from '../../../components/BarcodeScanner';
import { IconBalance, IconRefresh, IconScan } from '../../../components/icons/AnalyzerIcons';
import { useScannerFocusTrap } from '../../../hooks/useScannerFocusTrap';
import type { FavoriteProduct } from '../../../types/api';
import { createProductReference } from '../../../utils/productIdentity';
import { FavoriteProductPicker } from '../FavoriteProductPicker';
import { HistoryProductPicker } from '../HistoryProductPicker';
import type { SideProduct } from './reducer';

const MIN_BARCODE_DIGITS = 8;
const cleanBarcode = (v: string): string => v.replace(/\D/g, '').slice(0, 14);

export const PHOTO_PLAN_MESSAGE =
  'Photo search is available on Plus and Premium. Use the barcode or your favorites instead.';

export interface SideEntryProps {
  side: 'a' | 'b';
  label: string;
  initialBarcode?: string;
  /** Cost of identifying a new product, or null when unknown. */
  identifyCost: number | null;
  isPlusOrPremium: boolean;
  excludedProductId?: number;
  /** Focus the first source button on mount (the side was just cleared). */
  focusOnMount?: boolean;
  onBarcode: (code: string) => void;
  onPhoto: (file: Blob) => void;
  onFavorite: (product: SideProduct) => void;
}

function BarcodeOverlay({ label, onDetected, onClose }: { label: string; onDetected: (code: string) => void; onClose: () => void }) {
  const ref = useScannerFocusTrap<HTMLDivElement>(true, onClose);
  // BarcodeScanner restarts the camera when its callbacks change, so hand it stable ones.
  const detectedRef = useRef(onDetected);
  const closeRef = useRef(onClose);
  useEffect(() => {
    detectedRef.current = onDetected;
    closeRef.current = onClose;
  });
  const detected = useCallback((code: string) => { detectedRef.current(code); }, []);
  const fallback = useCallback(() => { closeRef.current(); }, []);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="fixed inset-0 z-50 bg-black/80 overscroll-none outline-none flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={`Scan ${label} barcode`}
    >
      <div className="flex items-center justify-between px-4 py-3 bg-black/40">
        <span className="t-small" style={{ color: 'rgba(248,244,238,0.9)' }}>Align the barcode within the frame</span>
        <button type="button" onClick={onClose} className="btn btn--hud btn--sm">Close</button>
      </div>
      <div className="flex-1 min-h-0 relative">
        <BarcodeScanner onDetected={detected} onFallback={fallback} />
      </div>
    </div>
  );
}

/** Empty side: favorites, scan history, barcode (typed, pasted or camera) or a photo. */
export default function SideEntry({
  side, label, initialBarcode, identifyCost, isPlusOrPremium, excludedProductId, focusOnMount, onBarcode, onPhoto, onFavorite,
}: SideEntryProps) {
  const [barcode, setBarcode] = useState(cleanBarcode(initialBarcode ?? ''));
  const [editing, setEditing] = useState(Boolean(initialBarcode));
  const [scanning, setScanning] = useState(false);
  const [picking, setPicking] = useState(false);
  const [pickingHistory, setPickingHistory] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const firstSourceRef = useRef<HTMLButtonElement>(null);
  const barcodeRef = useRef<HTMLInputElement>(null);
  /** Barcode was pressed: focus the input once it renders. */
  const focusBarcodeRef = useRef(false);
  const inputId = `side-barcode-${side}`;
  const canFind = barcode.length >= MIN_BARCODE_DIGITS;

  const find = () => { if (canFind) onBarcode(barcode); };

  // A cleared side lands focus on its entry, not on <body>.
  useEffect(() => {
    if (focusOnMount) firstSourceRef.current?.focus();
  }, [focusOnMount]);
  useEffect(() => {
    if (!editing || !focusBarcodeRef.current) return;
    focusBarcodeRef.current = false;
    barcodeRef.current?.focus();
  }, [editing]);
  const openBarcode = () => {
    if (editing) {
      barcodeRef.current?.focus();
      return;
    }
    focusBarcodeRef.current = true;
    setEditing(true);
  };
  const closeScanner = useCallback(() => { setScanning(false); }, []);

  const pickFavorite = (product: FavoriteProduct) => {
    setPicking(false);
    onFavorite({
      productId: product.product_id,
      name: product.name,
      brand: product.brand || undefined,
      imageUrl: product.image_url ?? undefined,
      verified: false,
    });
  };

  const paste = async () => {
    try {
      const digits = cleanBarcode(await navigator.clipboard.readText());
      if (digits) setBarcode(digits);
    } catch { /* clipboard unavailable */ }
  };

  const openPhoto = () => {
    if (!isPlusOrPremium) {
      setNotice(PHOTO_PLAN_MESSAGE);
      return;
    }
    photoRef.current?.click();
  };

  return (
    <div className="stack-3">
      <div className="compare-slot__source-grid">
        <button ref={firstSourceRef} type="button" className="btn btn--ghost btn--sm justify-center" onClick={() => { setPicking(true); }} aria-label={`Choose ${label} from favorites`}>
          <IconBalance /> Favorites
        </button>
        <button type="button" className="btn btn--ghost btn--sm justify-center" onClick={() => { setPickingHistory(true); }} aria-label={`Choose ${label} from scan history`}>
          <IconRefresh /> History
        </button>
        <button type="button" className="btn btn--ghost btn--sm justify-center" onClick={openBarcode} aria-label={`Add ${label} barcode`}>
          <span aria-hidden>#</span> Barcode
        </button>
        <button type="button" className="btn btn--ghost btn--sm justify-center" onClick={openPhoto} aria-label={`Identify ${label} from a photo`}>
          <IconScan /> Photo
        </button>
      </div>
      <input
        ref={photoRef}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        // The Photo button above is the control; this input is only its file dialog.
        aria-hidden="true"
        data-testid={`side-photo-input-${side}`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onPhoto(file);
          event.target.value = '';
        }}
      />

      {editing && (
        <div className="compare-slot__barcode-entry">
          <label htmlFor={inputId} className="t-small" style={{ fontWeight: 600 }}>{label} barcode</label>
          <div className="compare-barcode-field">
            <input
              ref={barcodeRef}
              id={inputId}
              value={barcode}
              onChange={(event) => { setBarcode(cleanBarcode(event.target.value)); }}
              onKeyDown={(event) => { if (event.key === 'Enter') find(); }}
              placeholder="Scan or enter barcode"
              inputMode="numeric"
              className="compare-barcode-field__input"
            />
            <div className="compare-barcode-field__actions">
              <button type="button" className="compare-barcode-field__btn" onClick={() => { setScanning(true); }} aria-label={`Scan ${label} barcode with camera`}>
                <IconScan />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { void paste(); }}>Paste</button>
            <button type="button" className="btn btn--primary btn--sm" disabled={!canFind} onClick={find}>Find product</button>
          </div>
        </div>
      )}

      {notice && <p className="t-small" role="note" style={{ margin: 0, color: 'var(--c-error)' }}>{notice}</p>}
      {identifyCost !== null && (
        <p className="t-small t-mute" style={{ margin: 0 }}>
          Identifying a new product costs {identifyCost} credits. Products you scanned before are free.
        </p>
      )}

      {scanning && (
        <BarcodeOverlay
          label={label}
          onClose={closeScanner}
          onDetected={(code) => {
            setScanning(false);
            const digits = cleanBarcode(code);
            if (digits) onBarcode(digits);
          }}
        />
      )}
      {picking && (
        <FavoriteProductPicker
          targetLabel={label}
          excludedProduct={excludedProductId ? createProductReference({ productId: excludedProductId }) : undefined}
          onSelect={pickFavorite}
          onClose={() => { setPicking(false); }}
        />
      )}
      {pickingHistory && (
        <HistoryProductPicker
          targetLabel={label}
          excludedProduct={excludedProductId ? createProductReference({ productId: excludedProductId }) : undefined}
          onSelect={(product) => {
            setPickingHistory(false);
            onFavorite(product);
          }}
          onClose={() => { setPickingHistory(false); }}
        />
      )}
    </div>
  );
}
